import React, { Component } from 'react';
import Select from './common/select';
import moment from 'moment';
import JOI from "joi-browser";

import { HTTPService } from "./../services/httpService";
import { SelectCustomerVehicle } from './partial/selectCustomerVehicle';
import { mintVin, ASSET_TYPES, VEHICLE_ASSET_TYPE, isPlaceholderVin, isMarineVehicle } from '../util/assetType';
import { isMarineEnabled } from '../util/marineCapability';
import { buildMarineAttributes, boatSizeTier } from '../util/marineService';
import { getBoatMakesForYear, getBoatModels } from '../services/marineReferenceService';

const API = new HTTPService();

export default class VehicleSelectForm extends Component {

    state = {
        data: { vin: " " },
        errors: {},
        years: [],
        selectedYear: "Year",
        makes: [],
        selectedMake: "Make",
        selectedMakeObj: null,
        models: [],
        selectedModel: "Model",
        selectedModelObj: null,
        trims: [],
        selectedTrim: "Trim",
        selectedTrimObj: null,
        bodies: [],
        selectedBody: "Style",
        selectedColor: "Color",
        selectedStock: "Stock #",
        selectedSize: "",
        selectedId: "",
        message: "",
        vehicle: "",
        custVehicles: [],
        showList: false,
        spec: "",
        loading: false,
        isVinDecoded: false,  // Track if selections came from VIN decode

        // --- Marine (boat) asset entry -------------------------------------
        // `forcedAssetType` types the whole wizard: a marine dealership's step 2
        // is boats-only and renders NO toggle — not a hidden one, none at all.
        // Absent (installer walk-in), assetType stays 'auto' unless the location
        // is marine-capable AND the estimator switches. Every marine branch below
        // is guarded on it, so a car-only shop sees byte-identical behaviour to
        // before.
        assetType: this.props.forcedAssetType || ASSET_TYPES.AUTO,
        boatMakes: [],
        selectedBoatMake: null,        // the make row { make_id, make_name }
        boatModels: [],
        selectedBoatModel: null,       // the model row { make_id, model_name, ... } —
                                        // carries dimensions + uuid; picking it is the
                                        // terminal step of the cascade
        selectedBoatYear: "Year",      // plain value from the shared `years` list —
                                        // NOT a reference row
        selectedBoatSize: "",          // 'S'|'M'|'L'|'XL'|'' — the estimator's pick, for By-size services
        boatVin: null,   // minted once per boat selection, never re-minted
        // Set only when restoring a previously-worked boat, so the estimate
        // links to the existing vehicle row instead of creating a duplicate.
        boatVehicleId: "",
        marineLoading: false,
        marineError: ""
    }

    schema = {
        vin: JOI.string().regex(/[A-HJ-NPR-Z0-9]{17}/).label('VIN #')
    };

// Hierarchical VIN Matching Algorithm - Match in order: Year → Make → Model → Trim → Style
vinMatchingAlgorithm = {
    
    // String normalization for better matching
    normalizeString: (text) => {
        if (!text) return "";
        return String(text)
            .toLowerCase()
            .replace(/[^\w\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    },

    // Simple string similarity
    stringSimilarity: (str1, str2) => {
        console.log("Comparing strings:", str1, "vs", str2);
        
        const s1 = this.vinMatchingAlgorithm.normalizeString(str1);
        const s2 = this.vinMatchingAlgorithm.normalizeString(str2);
        
        // Exact match gets highest score
        if (s1 === s2) {
            console.log("Exact match found");
            return 1.0;
        }
        
        // For model names, prioritize exact token matches
        if (str1 && str2) {
            const cleanStr1 = str1.replace(/[-\s]class/gi, '').replace(/\s+sedan/gi, '').trim();
            const cleanStr2 = str2.replace(/[-\s]class/gi, '').replace(/\s+sedan/gi, '').trim();
            
            if (cleanStr1.toLowerCase() === cleanStr2.toLowerCase()) {
                console.log("Clean exact match found");
                return 0.99;
            }
        }
        
        // Containment match with length consideration
        if (s1.includes(s2)) {
            const ratio = s2.length / s1.length;
            console.log("s1 contains s2, ratio:", ratio);
            return 0.8 + (ratio * 0.15);
        }
        if (s2.includes(s1)) {
            const ratio = s1.length / s2.length;
            console.log("s2 contains s1, ratio:", ratio);
            return 0.8 + (ratio * 0.15);
        }
        
        // Word-based matching for complex strings
        const words1 = s1.split(' ').filter(w => w.length > 0);
        const words2 = s2.split(' ').filter(w => w.length > 0);
        
        if (words1.length === 0 && words2.length === 0) return 1.0;
        if (words1.length === 0 || words2.length === 0) return 0.0;
        
        let matchingWords = 0;
        words1.forEach(w1 => {
            if (words2.some(w2 => {
                // Exact word match always valid
                if (w1 === w2) return true;
                // Containment/fuzzy only for words with 3+ chars to avoid
                // false matches like "c" in "class" or levenshtein("c","a")=1
                if (w1.length >= 3 && w2.length >= 3) {
                    if (w1.includes(w2) || w2.includes(w1)) return true;
                    if (this.vinMatchingAlgorithm.levenshteinDistance(w1, w2) <= 1) return true;
                }
                return false;
            })) {
                matchingWords++;
            }
        });
        
        const similarity = matchingWords / Math.max(words1.length, words2.length);
        console.log("String similarity result:", similarity);
        return similarity;
    },

    // Levenshtein distance
    levenshteinDistance: (str1, str2) => {
        const matrix = [];
        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }
        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }
        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        return matrix[str2.length][str1.length];
    },

    // Extract VIN data with improved model parsing
    extractVinData: (vinResults) => {
        console.log("Extracting VIN data from:", vinResults);
        
        const vinData = {};
        
        if (vinResults && typeof vinResults === 'object') {
            // Year - Level 1
            if (vinResults.ModelYear) {
                vinData.year = parseInt(vinResults.ModelYear);
                console.log("Extracted year:", vinData.year);
            }
            
            // Make - Level 2  
            if (vinResults.Make) {
                vinData.make = vinResults.Make;
                console.log("Extracted make:", vinData.make);
            }
            
            // Model - Level 3 (use NHTSA model name directly for accurate dropdown matching)
            if (vinResults.Model) {
                const modelString = vinResults.Model;
                vinData.fullModel = modelString;
                vinData.model = modelString;

                console.log("Model extraction:", modelString);
            }

            // Store Series for trim matching if available
            if (vinResults.Series) {
                vinData.series = vinResults.Series;
                console.log("Extracted series:", vinData.series);
            }
            
            // Trim - Level 4
            if (vinResults.Trim) {
                vinData.trim = vinResults.Trim;
            } else if (vinResults.Trim2) {
                vinData.trim = vinResults.Trim2;
            }
            
            // Extract trim from series if no explicit trim
            if (!vinData.trim && vinData.series) {
                const seriesParts = vinData.series.split(' ');
                if (seriesParts.length > 1) {
                    vinData.extractedTrim = seriesParts.slice(1).join(' ');
                    console.log("Extracted trim from series:", vinData.extractedTrim);
                }
            }
            
            // Body Class - Level 5
            if (vinResults.BodyClass) {
                vinData.bodyClass = vinResults.BodyClass;
                console.log("Extracted body class:", vinData.bodyClass);
            }
        }
        
        console.log("Final VIN data:", vinData);
        return vinData;
    },

    // LEVEL 1: Year matching (must match exactly)
    matchYear: (vinYear, selectedYear) => {
        console.log(`LEVEL 1 - Year matching: VIN year ${vinYear} vs selected ${selectedYear}`);
        return vinYear && selectedYear && vinYear.toString() === selectedYear.toString();
    },

    // LEVEL 2: Make matching (find best match)
    matchMake: (vinMake, availableMakes) => {
        console.log(`LEVEL 2 - Make matching: Looking for "${vinMake}" in ${availableMakes.length} makes`);
        
        if (!vinMake || !availableMakes) {
            console.log("No VIN make or available makes");
            return null;
        }
        
        // Find exact match first
        const exactMatch = availableMakes.find(make => 
            make.name.toLowerCase() === vinMake.toLowerCase()
        );
        
        if (exactMatch) {
            console.log("EXACT MAKE MATCH:", exactMatch.name);
            return exactMatch;
        }
        
        // Find best similarity match
        const matches = availableMakes.map(make => ({
            ...make,
            score: this.vinMatchingAlgorithm.stringSimilarity(vinMake, make.name)
        }))
        .filter(make => make.score > 0.8) // High threshold for make matching
        .sort((a, b) => b.score - a.score);
        
        const bestMatch = matches[0] || null;
        if (bestMatch) {
            console.log("BEST MAKE MATCH:", bestMatch.name, "Score:", bestMatch.score);
        } else {
            console.log("NO SUITABLE MAKE MATCH FOUND");
        }
        
        return bestMatch;
    },

    // LEVEL 3: Model matching (find best match within the make)
    matchModel: (vinModel, vinFullModel, availableModels) => {
        console.log(`LEVEL 3 - Model matching: Looking for "${vinModel}" in ${availableModels.length} models`);
        console.log("Available models:", availableModels.map(m => m.name));
        
        if (!vinModel || !availableModels || availableModels.length === 0) {
            console.log("No VIN model or available models");
            return null;
        }
        
        // Find exact match first (highest priority)
        const exactMatch = availableModels.find(model => 
            model.name.toLowerCase() === vinModel.toLowerCase()
        );
        
        if (exactMatch) {
            console.log("EXACT MODEL MATCH FOUND:", exactMatch.name);
            return exactMatch;
        }
        
        // Find best similarity match
        const matches = availableModels.map(model => {
            const modelName = String(model.name);
            
            // Try base model match
            const baseScore = this.vinMatchingAlgorithm.stringSimilarity(vinModel, modelName);
            
            // Try full model match if different
            let fullScore = 0;
            if (vinFullModel && vinFullModel !== vinModel) {
                fullScore = this.vinMatchingAlgorithm.stringSimilarity(vinFullModel, modelName);
            }
            
            const bestScore = Math.max(baseScore, fullScore);
            console.log(`Model "${modelName}": base=${baseScore}, full=${fullScore}, best=${bestScore}`);
            
            return {
                ...model,
                score: bestScore
            };
        })
        .filter(model => model.score > 0.7) // High threshold for model matching
        .sort((a, b) => b.score - a.score);
        
        const bestMatch = matches[0] || null;
        if (bestMatch) {
            console.log("BEST MODEL MATCH:", bestMatch.name, "Score:", bestMatch.score);
        } else {
            console.log("NO SUITABLE MODEL MATCH FOUND");
        }
        
        return bestMatch;
    },

    // LEVEL 4: Trim matching (find best match within the model)
    matchTrim: (vinData, availableTrims) => {
        console.log(`LEVEL 4 - Trim matching: Looking for trim in ${availableTrims.length} options`);
        console.log("Available trims:", availableTrims.map(t => `${t.name} (${t.body_style}, ${t.start_production_year}-${t.end_production_year})`));
        
        if (!availableTrims || availableTrims.length === 0) {
            console.log("No available trims");
            return null;
        }
        
        // If only one trim, select it (common case)
        if (availableTrims.length === 1) {
            const singleTrim = availableTrims[0];
            console.log("SINGLE TRIM AVAILABLE - Auto-selecting:", singleTrim.name);
            
            // Check year compatibility
            if (vinData.year) {
                const startYear = parseInt(singleTrim.start_production_year) || 0;
                const endYear = parseInt(singleTrim.end_production_year) || 9999;
                const yearMatch = startYear <= vinData.year && vinData.year <= endYear;
                console.log(`Year compatibility: ${startYear} <= ${vinData.year} <= ${endYear} = ${yearMatch}`);
                
                if (!yearMatch) {
                    console.log("WARNING: Year mismatch but selecting anyway (single trim)");
                }
            }
            
            return singleTrim;
        }
        
        // Multiple trims - find best match
        const scoredTrims = availableTrims.map(trim => {
            let score = 0;
            const factors = [];
            
            // Year compatibility (mandatory)
            let yearScore = 0;
            if (vinData.year) {
                const startYear = parseInt(trim.start_production_year) || 0;
                const endYear = parseInt(trim.end_production_year) || 9999;
                if (startYear <= vinData.year && vinData.year <= endYear) {
                    yearScore = 1.0;
                    factors.push(`year:✓`);
                } else {
                    factors.push(`year:✗`);
                }
            }
            
            // Trim name similarity
            let trimScore = 0;
            if (vinData.trim) {
                trimScore = Math.max(trimScore, this.vinMatchingAlgorithm.stringSimilarity(vinData.trim, trim.name));
            }
            if (vinData.extractedTrim) {
                trimScore = Math.max(trimScore, this.vinMatchingAlgorithm.stringSimilarity(vinData.extractedTrim, trim.name));
            }
            if (vinData.series) {
                trimScore = Math.max(trimScore, this.vinMatchingAlgorithm.stringSimilarity(vinData.series, trim.name));
            }
            factors.push(`trim:${trimScore.toFixed(2)}`);
            
            // Body style similarity
            let bodyScore = 0;
            if (vinData.bodyClass && trim.body_style) {
                bodyScore = this.vinMatchingAlgorithm.stringSimilarity(vinData.bodyClass, trim.body_style);
                factors.push(`body:${bodyScore.toFixed(2)}`);
            }
            
            // Final score calculation
            score = (yearScore * 0.5) + (trimScore * 0.3) + (bodyScore * 0.2);
            
            console.log(`Trim "${trim.name}": ${factors.join(', ')} = ${score.toFixed(3)}`);
            
            return {
                ...trim,
                score: score,
                yearMatch: yearScore === 1.0
            };
        })
        .filter(trim => trim.yearMatch) // Year compatibility is mandatory
        .sort((a, b) => b.score - a.score);
        
        const bestTrim = scoredTrims[0] || null;
        if (bestTrim) {
            console.log("BEST TRIM MATCH:", bestTrim.name, "Score:", bestTrim.score.toFixed(3));
        } else {
            console.log("NO YEAR-COMPATIBLE TRIM FOUND - trying fallback");
            // Fallback: return highest scoring trim regardless of year
            const fallbackTrims = availableTrims.map(trim => ({
                ...trim,
                score: 0.1 // Low score for fallback
            })).sort((a, b) => a.name.localeCompare(b.name));
            
            const fallback = fallbackTrims[0] || null;
            if (fallback) {
                console.log("FALLBACK TRIM SELECTED:", fallback.name);
            }
            return fallback;
        }
        
        return bestTrim;
    },

    // LEVEL 5: Body style matching (find best match within the trim)
    matchBodyStyle: (vinData, availableBodyStyles) => {
        console.log(`LEVEL 5 - Body style matching: Looking for "${vinData.bodyClass}" in:`, availableBodyStyles);
        
        if (!vinData.bodyClass || !availableBodyStyles || availableBodyStyles.length === 0) {
            console.log("No VIN body class or available body styles");
            return availableBodyStyles ? availableBodyStyles[0] : null;
        }
        
        // If only one body style, select it
        if (availableBodyStyles.length === 1) {
            console.log("SINGLE BODY STYLE - Auto-selecting:", availableBodyStyles[0]);
            return availableBodyStyles[0];
        }
        
        // Find best match
        const matches = availableBodyStyles.map(bodyStyle => ({
            name: bodyStyle,
            score: this.vinMatchingAlgorithm.stringSimilarity(vinData.bodyClass, bodyStyle)
        }))
        .sort((a, b) => b.score - a.score);
        
        const bestMatch = matches[0];
        console.log("BEST BODY STYLE MATCH:", bestMatch.name, "Score:", bestMatch.score.toFixed(3));
        
        return bestMatch.name;
    }
};

// Enhanced getVIN function with hierarchical matching
async getVIN(vinId) {
    console.log("=== STARTING HIERARCHICAL VIN MATCHING ===");
    this.setState({ loading: true, isVinDecoded: true });
    
    try {
        // Get vehicle data from NHTSA
        console.log("Step 1: Fetching VIN data from NHTSA...");
        const { data: vehicle } = await API.get(`/vin/?Id=${vinId}`);
        const nhtsaResults = vehicle.Results?.[0] || vehicle;

        console.log("NHTSA Results received:", nhtsaResults);

        // Extract VIN data for hierarchical matching
        console.log("Step 2: Extracting VIN data...");
        const vinData = this.vinMatchingAlgorithm.extractVinData(nhtsaResults);

        // NHTSA sometimes returns only a model year with empty make/model
        // (seen with Genesis GV80 among others). We can't validate that partial
        // data, so don't apply ANY of it — leave the form as the user had it
        // and let them fill in manually. Bail BEFORE any setState so we don't
        // half-update selectedYear / selectedMake / selectedModel.
        const decodedMake = (vinData.make || '').trim();
        const decodedModel = (vinData.model || '').trim();
        if (!vinData.year || !decodedMake || !decodedModel) {
            console.warn(
                "NHTSA returned incomplete VIN data — keeping existing form values.",
                { year: vinData.year, make: decodedMake, model: decodedModel }
            );
            this.setState({ loading: false, isVinDecoded: false });
            return;
        }

        // Decode passed validation — now safe to record the raw result for
        // downstream fallback paths.
        this.setState({ vehicle: nhtsaResults });

        console.log("=== LEVEL 1: YEAR MATCHING ===");
        // Set the year (Level 1 - must match exactly)
        const yearString = vinData.year.toString();
        this.setState({ selectedYear: yearString });
        console.log(`Year set to: ${yearString}`);
        
        // Load makes for the year
        console.log("Loading makes for year:", yearString);
        await this.readMakes(yearString);
        
        console.log("=== LEVEL 2: MAKE MATCHING ===");
        // Find matching make (Level 2)
        const makeMatch = this.vinMatchingAlgorithm.matchMake(vinData.make, this.state.makes);
        
        if (!makeMatch) {
            console.error("LEVEL 2 FAILED: No suitable make match found");
            await this.fallbackVINSelection(nhtsaResults);
            this.setState({ loading: false });
            return;
        }
        
        console.log(`LEVEL 2 SUCCESS: Selected make - ${makeMatch.name}`);
        this.setState({ 
            selectedMake: makeMatch.name,
            selectedMakeObj: makeMatch  // ADD THIS LINE
        });        
        // Load models for the matched make
        console.log("Loading models for make:", makeMatch.name);
        await this.readModels([makeMatch]);
        
        console.log("=== LEVEL 3: MODEL MATCHING ===");
        // Find matching model (Level 3)
        const modelMatch = this.vinMatchingAlgorithm.matchModel(
            vinData.model, 
            vinData.fullModel, 
            this.state.models
        );
        
        if (!modelMatch) {
            console.error("LEVEL 3 FAILED: No suitable model match found");
            await this.fallbackModelSelection(vinData);
            this.setState({ loading: false });
            return;
        }
        console.log(`LEVEL 3 SUCCESS: Selected model - ${modelMatch.name}`);
        this.setState({ 
            selectedModel: modelMatch.name,
            selectedModelObj: modelMatch  // ADD THIS LINE  
        });        
        // Load trims for the matched model
        console.log("Loading trims for model:", modelMatch.name);
        await this.readTrims([modelMatch]);
        
        console.log("=== LEVEL 4: TRIM MATCHING ===");
        // Find matching trim (Level 4)
        const trimMatch = this.vinMatchingAlgorithm.matchTrim(vinData, this.state.trims);
        
        if (!trimMatch) {
            console.error("LEVEL 4 FAILED: No suitable trim match found");
            // Don't fail completely - might still be able to manually select
            this.setState({ loading: false });
            return;
        }
        
        console.log(`LEVEL 4 SUCCESS: Selected trim - ${trimMatch.name}`);
        this.setState({ 
            selectedTrim: trimMatch.name,
            selectedTrimObj: trimMatch
        });
        
        console.log("=== LEVEL 5: BODY STYLE MATCHING ===");
        // Find matching body style (Level 5)
        const availableBodyStyles = [...new Set(
            this.state.trims
                .filter(trim => trim.name === trimMatch.name)
                .map(trim => trim.body_style)
        )];
        
        console.log("Available body styles for trim:", availableBodyStyles);
        
        const bodyStyleMatch = this.vinMatchingAlgorithm.matchBodyStyle(vinData, availableBodyStyles);
        
        if (bodyStyleMatch) {
            console.log(`LEVEL 5 SUCCESS: Selected body style - ${bodyStyleMatch}`);
            
            // Auto-select the body style
            setTimeout(() => {
                const fakeEvent = { target: { value: bodyStyleMatch } };
                this.handleBodyChange(fakeEvent);
                console.log("=== HIERARCHICAL MATCHING COMPLETE ===");
            }, 100);
        } else {
            console.log("LEVEL 5: No specific body style match, user will select manually");
        }
        
        this.setState({ loading: false });
        console.log("=== VIN MATCHING PROCESS COMPLETED SUCCESSFULLY ===");
        
    } catch (error) {
        console.error("ERROR in hierarchical VIN matching:", error);
        console.log("=== FALLING BACK TO ORIGINAL METHOD ===");
        
        try {
            // nhtsaResults might not be defined if error occurred early, so check
            if (this.state.vehicle) {
                await this.fallbackVINSelection(this.state.vehicle);
            } else {
                console.error("No NHTSA results available for fallback");
            }
        } catch (fallbackError) {
            console.error("Fallback also failed:", fallbackError);
        }
        
        this.setState({ loading: false, isVinDecoded: false });
    }
}

checkForDuplicateVehicle = async (vehicle) => {
  try {
    const checkData = {
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      trim: vehicle.trim,
      vin: vehicle.vin, // ADD VIN to the check
      userType: this.props.userType,
      customerType: this.props.customerType || '',
      // For WalkInForm: customer ID (could be dealer or retail customer)
      // For RequestimateForm: dealer ID  
      userId: this.props.customer || this.props.typeId,
      // For RequestimateForm, we also need the actual customer (dealer's customer)
      dealerCustomerId: this.props.userType === 'dealer' ? this.props.customer : null
    };
    
    console.log('Checking for duplicate vehicle:', checkData);
    const { data: duplicates } = await API.post('/requests/check-duplicate', checkData);
    return duplicates || [];
  } catch (error) {
    console.error('Error checking for duplicates:', error);
    return [];
  }
};

// Enhanced fallback methods
async fallbackVINSelection(nhtsaResults) {
    console.log("=== FALLBACK: Using original VIN selection method ===");
    
    if (!nhtsaResults || !nhtsaResults.Make || !nhtsaResults.Model || !nhtsaResults.ModelYear) {
        console.log("Insufficient NHTSA data for fallback");
        return;
    }
    
    const { Make, Model, ModelYear } = nhtsaResults;
    console.log(`Fallback matching: ${ModelYear} ${Make} ${Model}`);
    
    // Set the year
    this.setState({ selectedYear: ModelYear });
    
    // Find make using normalized matching
    const searchMake = this.normalizeString(Make);
    const filteredMakes = this.state.makes.filter(item => {
        const fieldValue = this.normalizeString(item.name);
        return fieldValue === searchMake;
    });
    
    if (filteredMakes.length > 0) {
        console.log("Fallback: Found make match:", filteredMakes[0].name);
        this.setState({ 
            selectedMake: filteredMakes[0].name,
            selectedMakeObj: filteredMakes[0]  // ADD THIS LINE
        });
        await this.readModels(filteredMakes);
        
        setTimeout(async () => {
            const searchModel = this.normalizeString(Model);
            const filteredModels = this.state.models.filter(item => {
                const fieldValue = this.normalizeString(item.name);
                return fieldValue === searchModel;
            });
            
            if (filteredModels.length > 0) {
                console.log("Fallback: Found model match:", filteredModels[0].name);
                this.setState({ 
                    selectedModel: filteredModels[0].name,
                    selectedModelObj: filteredModels[0]  // ADD THIS LINE
                });
                await this.readTrims(filteredModels);
            }
        }, 200);
    }
}

async fallbackModelSelection(vinData) {
    console.log("=== FALLBACK: Model selection ===");
    
    if (!vinData.model) {
        console.log("No model data for fallback");
        return;
    }
    
    const searchModel = this.normalizeString(vinData.model);
    const filteredModels = this.state.models.filter(item => {
        const fieldValue = this.normalizeString(item.name);
        return fieldValue === searchModel;
    });
    
    if (filteredModels.length > 0) {
        console.log("Fallback: Found model match:", filteredModels[0].name);
        this.setState({ selectedModel: filteredModels[0].name });
        await this.readTrims(filteredModels);
    } else {
        console.log("Fallback: No model match found");
    }
}


    async componentDidMount() {
        const end = new moment().add(1, 'years').format("YYYY");
        const start = 1950;
        var years = this.fillRange(start, end);
        years.sort((a, b) => b - a);
        years.unshift("Year");
        this.setState({ years });
        // In VehicleSelectForm componentDidMount, enhance the logging:
        if (this.props.vehicle && (this.props.vehicle.vin || this.props.vehicle.make)) {
            const v = this.props.vehicle;
            // Resume/prefill: when the vehicle already carries its decoded details
            // (make + model), restore them directly. Re-decoding the VIN here would
            // wipe a manually-picked vehicle whose VIN is a placeholder/non-decodable
            // (common for walk-ins). Only decode when we have a bare VIN and no
            // make/model yet (the referral hand-off case).
            if (v.make && v.model) {
                console.log("VehicleSelectForm - Restoring saved vehicle:", v);
                await this.restoreVehicleState(v);
            } else if (v.vin) {
                console.log("VehicleSelectForm - Processing referral vehicle with VIN:", v.vin);
                this.setState({
                    data: { vin: v.vin },
                    isVinDecoded: true
                }, async () => {
                    if (!this.validate()) {
                        await this.doSubmit();
                    } else {
                        await this.restoreVehicleState(v);
                    }
                });
            }
        }
    }

async componentDidUpdate(prevProps) {
    // Re-seed on ANY change of the forced type, including MARINE -> null when a
    // group user switches to an automotive dealership. Gating on the NEW value
    // being truthy would leave the wizard stuck in boat mode with no toggle to
    // recover, because isMarineEnabled() is false for every dealer scope.
    //
    // Reuse handleAssetTypeChange so BOTH cascades are reset — a half-filled car
    // selection must never leak into a boat payload, or the reverse.
    const forcedTarget = this.props.forcedAssetType || ASSET_TYPES.AUTO;
    if (prevProps.forcedAssetType !== this.props.forcedAssetType &&
        this.state.assetType !== forcedTarget) {
        this.handleAssetTypeChange(forcedTarget);
    }

    // Existing customer logic
    if ((this.props.customer !== prevProps.customer) || (this.props.customerType !== prevProps.customerType)) {
        var vehiclesRequest = this.props.customer + "/" + this.props.installer.Installer_id;
        const { data: custVehicles } = await API.get(`/vehicles/${vehiclesRequest}`);

        // Deduplicate vehicles based on Year, Make, Model, Trim, and body
        const uniqueVehicles = custVehicles.reduce((acc, vehicle) => {
            const key = `${vehicle.Year}-${vehicle.Make}-${vehicle.Model}-${vehicle.Trim}-${vehicle.body}`;
            if (!acc.seen.has(key)) {
                acc.seen.add(key);
                acc.vehicles.push(vehicle);
            }
            return acc;
        }, { seen: new Set(), vehicles: [] }).vehicles;

        this.setState({ custVehicles: uniqueVehicles });
        if (uniqueVehicles.length > 0 && this.props.customerType === 'retail')
            this.setState({ showList: true });
    }

    // Handle vehicle from referral/rebook - prioritize VIN decode
    if (this.props.vehicle &&
        this.props.vehicle !== prevProps.vehicle &&
        this.props.vehicle.vin &&
        this.state.data.vin !== this.props.vehicle.vin) { // Check if we haven't processed this VIN yet

        console.log("VehicleSelectForm - Processing vehicle with VIN:", this.props.vehicle);

        // Set VIN and trigger decode
        this.setState({
            data: { vin: this.props.vehicle.vin },
            isVinDecoded: true
        }, async () => {
            console.log("Triggering VIN decode for vehicle");

            // Trigger VIN decode using proper validation
            if (!this.validate()) {
                await this.doSubmit();
            } else {
                console.log("VIN validation failed, falling back to manual selection");
                // Fallback to manual dropdown population using year/make/model if available
                await this.restoreVehicleState(this.props.vehicle);
                // If body was restored, trigger handleBodyChange to pass vehicle to parent
                if (this.props.vehicle.body && this.props.vehicle.trim) {
                    setTimeout(() => {
                        const fakeEvent = { target: { value: this.state.selectedBody } };
                        if (this.state.selectedBody && this.state.selectedBody !== "Style") {
                            this.handleBodyChange(fakeEvent);
                        }
                    }, 200);
                }
            }
        });
    }

    // Handle vehicle state restoration (no VIN or empty VIN — use year/make/model fields)
    else if (this.props.vehicle &&
             this.props.vehicle !== prevProps.vehicle &&
             this.props.vehicle.year &&
             !this.props.vehicle.vin) {

        console.log("VehicleSelectForm - Restoring vehicle state from fields:", this.props.vehicle);
        await this.restoreVehicleState(this.props.vehicle);
    }
}
    restoreVehicleState = async (vehicle) => {
    console.log("Restoring vehicle state:", vehicle);
    
    // Restore year
    if (vehicle.year) {
        this.setState({ selectedYear: vehicle.year.toString() });
        await this.readMakes(vehicle.year.toString());
        
        // Restore make
        if (vehicle.make) {
            const makeMatch = this.state.makes.find(make => 
                make.name.toLowerCase() === vehicle.make.toLowerCase()
            );
            
            if (makeMatch) {
        this.setState({ 
            selectedMake: vehicle.make,
            selectedMakeObj: makeMatch  // ADD THIS LINE
        });
                        await this.readModels([makeMatch]);
                
                // Restore model
                if (vehicle.model) {
                    const modelMatch = this.state.models.find(model =>
                        model.name.toLowerCase() === vehicle.model.toLowerCase()
                    );
                    
                    if (modelMatch) {
                        this.setState({ 
                            selectedModel: vehicle.model,
                            selectedModelObj: modelMatch,  // ADD THIS LINE
                            selectedSize: modelMatch.vehicleSize || ""
                        });
                        await this.readTrims([modelMatch]);
                        
                        // Restore trim and body
                        if (vehicle.trim) {
                            const trimMatch = this.state.trims.find(trim => 
                                trim.name === vehicle.trim
                            );
                            
                            this.setState({ 
                                selectedTrim: vehicle.trim,
                                selectedTrimObj: trimMatch
                            });
                            
                            if (vehicle.body) {
                                this.setState({ selectedBody: vehicle.body });
                            }
                        }
                    }
                }
            }
        }
    }
    
    // Restore VIN if present
    if (vehicle.vin && vehicle.vin !== "") {
        this.setState({ 
            data: { vin: vehicle.vin },
            vehicle: vehicle
        });
    }
    
    console.log("Vehicle state restoration complete");
};

    validate = () => {
        const options = { abortEarly: false };
        const { error } = JOI.validate(this.state.data, this.schema, options);
        if (!error) return null;
        const errors = {};
        for (let item of error.details) errors[item.path[0]] = item.message;
        return errors;
    };

    validateProperty = ({ name, value }) => {
        const obj = { [name]: value };
        const schema = { [name]: this.schema[name] };
        const { error } = JOI.validate(obj, schema);
        return error ? error.details[0].message : null;
    };

    handleChange = ({ currentTarget: input }) => {
        const errors = { ...this.state.errors };
        const errorMessage = this.validateProperty(input);
        if (errorMessage) errors[input.name] = errorMessage;
        else delete errors[input.name];

        const data = { ...this.state.data };

        if (input.name === 'vin') {
            // Convert VIN to uppercase and trim leading/trailing spaces
            data[input.name] = input.value.toUpperCase().trim();
        } else {
            data[input.name] = input.value;
        }

        this.setState({ data, errors });
    };

    readVIN = async (vin) => {
        let vinRequest = "/vehiclesVIN/" + vin;
        const { data: spec } = await API.get(vinRequest);
        this.setState({ spec });
        return spec;
    }

    readMakes = async (yearSelect) => {
        //console.log("Reading makes for year:", yearSelect);
        let makesRequest = "/makes/" + yearSelect;
        const { data: makes } = await API.get(makesRequest);
        this.setState({ makes });
        if (this.props.default) {
            const fakeEvent = { target: { value: this.props.default } };
            this.handleMakeChange(fakeEvent);
        }
    }

    readModels = async (makeId) => {
        //console.log("Reading models for make:", makeId);
        let vehicleRequest = this.state.selectedYear + makeId[0].id_car_make;
        let modelsRequest = "/models/" + vehicleRequest;
        const { data: models } = await API.get(modelsRequest);
        this.setState({ models });
        
        // Only auto-select if it's a VIN decode or if there's only one model
        if (models.length === 1 && this.state.isVinDecoded) {
            const fakeEvent = { target: { value: models[0].name } };
            this.handleModelChange(fakeEvent);
        } else if (models.length === 1) {
            // Still auto-select single models for user convenience
            const fakeEvent = { target: { value: models[0].name } };
            this.handleModelChange(fakeEvent);
        }
    }

    readTrims = async (modelId) => {
        //console.log("Reading trims for model:", modelId);
        let trimRequest = modelId[0].id_car_model;
        let trimsRequest = "/trims/" + trimRequest;
        const { data: trims } = await API.get(trimsRequest);
        this.setState({ trims });
        
        // Only auto-select if user hasn't manually changed selections or if there's only one unique trim
        const uniqueTrims = [...new Set(trims.map(item => item.name))];
        if (uniqueTrims.length === 1 && (this.state.isVinDecoded || this.state.selectedTrim === "Trim")) {
            const fakeEvent = { target: { value: uniqueTrims[0] } };
            this.handleTrimChange(fakeEvent);
        }
    }

// In VehicleSelectForm, add debug logging to the getVIN method
// Look for where the process might be failing

// Also add logging to see if auto-selection is working
/**
 * Push the CURRENT automotive selection up to the parent.
 *
 * The parent's Next button gates on the vehicle object it holds. Until this
 * existed, only handleBodyChange emitted, so changing Year/Make/Model/Trim left
 * the parent holding the last COMPLETE vehicle: deselecting a trim on a
 * previously-entered vehicle left Next enabled and let a half-specified vehicle
 * through to pricing.
 *
 * Emits the partial shape deliberately — the parent decides completeness via
 * `isAutoVehicleComplete`, this component only reports what is selected. The
 * dropdown placeholders ('Trim', 'Style', …) are passed through as-is; the
 * predicate treats them as unselected.
 */
emitAutoVehicleState = () => {
    if (this.state.assetType === ASSET_TYPES.MARINE) return;
    const trimObj = this.state.selectedTrimObj;
    const vehicle = {
        id: this.state.vehicle?.id || "",
        year: this.state.selectedYear,
        make: this.state.selectedMake,
        model: this.state.selectedModel,
        trim: this.state.selectedTrim,
        id_car_make: this.state.selectedMakeObj?.id || this.state.selectedMakeObj?.id_car_make || null,
        id_car_model: this.state.selectedModelObj?.id || this.state.selectedModelObj?.id_car_model || null,
        id_car_trim: trimObj?.id_car_trim || trimObj?.id || null,
        body: this.state.selectedBody,
        color: this.state.selectedColor,
        series: "",
        vin: this.state.data?.vin || "",
        stock: this.state.selectedStock,
        size: trimObj ? trimObj.id_car_type : "",
        body_style: trimObj?.body_style || null,
        img_url: ""
    };
    this.setState({ vehicle });
    this.props.handleVIN(vehicle);
};

handleTrimChange = (event) => {
    const { value } = event.target;
    const selectedTrimObj = this.state.trims.find(trim => trim.name === value);
    
    console.log("handleTrimChange called with:", value);
    console.log("selectedTrimObj:", selectedTrimObj);
    
    this.setState({
        selectedTrim: value,
        selectedTrimObj: selectedTrimObj,
        selectedBody: "Style", // Reset body style when trim changes
        selectedColor: "Color", // Reset color when trim changes
        isVinDecoded: false // Reset VIN decode flag when user makes manual changes
    }, this.emitAutoVehicleState);
    
    // Auto-select body style if there's only one unique option for this trim
    const trimSpecificTrims = this.state.trims.filter(trim => trim.name === value);
    const uniqueBodyStyles = [...new Set(trimSpecificTrims.map(item => item.body_style))];
    
    console.log("Available body styles for trim:", uniqueBodyStyles);
    
    if (uniqueBodyStyles.length === 1) {
        console.log("Auto-selecting single body style:", uniqueBodyStyles[0]);
        const fakeEvent = { target: { value: uniqueBodyStyles[0] } };
        setTimeout(() => { 
            console.log("Triggering handleBodyChange with:", uniqueBodyStyles[0]);
            this.handleBodyChange(fakeEvent); 
        }, 100); // Increased delay
    } else {
        console.log("Multiple body styles available, waiting for user selection");
    }
}
    fillRange = (start, end) =>
        [...Array(end - start + 1)].map((item, index) => start + index);

    handleYearChange = (event) => {
        const { value } = event.target;
        this.setState({
            selectedYear: value,
            selectedMake: "Make",
            selectedModel: "Model",
            selectedTrim: "Trim",
            selectedBody: "Style",
            selectedColor: "Color",
            selectedMakeObj: null,      // Reset make object
            selectedModelObj: null,     // Reset model object
            selectedTrimObj: null,      // Reset trim object
            makes: [],
            models: [],
            trims: [],
            bodies: [],
            isVinDecoded: false
        }, this.emitAutoVehicleState);
        if (value !== "Year") {
            this.readMakes(value);
        }
    }

    handleMakeChange = (event) => {
        const { value } = event.target;
        const selectedMakeObj = this.state.makes.find(make => make.name === value);

            // DEBUG: Check the actual make object structure
    console.log("Selected make object:", selectedMakeObj);
    console.log("Available properties:", Object.keys(selectedMakeObj || {}));
        
        this.setState({
            selectedMake: value,
            selectedMakeObj: selectedMakeObj, // Store the full make object
            selectedModel: "Model",
            selectedTrim: "Trim",
            selectedBody: "Style",
            selectedColor: "Color",
            selectedModelObj: null, // Reset model object
            models: [],
            trims: [],
            bodies: [],
            selectedTrimObj: null,
            isVinDecoded: false
        }, this.emitAutoVehicleState);

        if (value !== "Make") {
            this.readModels([selectedMakeObj]); // Pass the object, not filtered array
        }
    }

    handleModelChange = (event) => {
        const { value } = event.target;
        const selectedModelObj = this.state.models.find(model => model.name === value);

            // DEBUG: Check the actual model object structure  
    console.log("Selected model object:", selectedModelObj);
    console.log("Available properties:", Object.keys(selectedModelObj || {}));
        
        this.setState({
            selectedModel: value,
            selectedModelObj: selectedModelObj, // Store the full model object
            selectedTrim: "Trim",
            selectedBody: "Style",
            selectedColor: "Color",
            selectedSize: selectedModelObj?.vehicleSize || "",
            trims: [],
            bodies: [],
            selectedTrimObj: null,
            isVinDecoded: false
        }, this.emitAutoVehicleState);

        if (value !== "Model") {
            this.readTrims([selectedModelObj]); // Pass the object, not filtered array
        }
    }
    
    normalizeString = (str) => {
        return str
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .trim();
    }

    handleClose = () => {
        this.setState({ showList: false });
    }

    doSubmit = async () => {
        // Trim and strip all spaces from VIN before submitting to API
        const cleanVin = this.state.data.vin.trim().replace(/\s+/g, '');
        await this.getVIN(cleanVin);
    };

    handleStockChange = (event) => {
        const { value } = event.target;
        this.setState({ stockNumber: value });

        // Pass to parent
        if (this.props.onStockNumberChange) {
            this.props.onStockNumberChange(value);
        }
    };

    handleColorChange = (event) => {
        const { value } = event.target;
        const color = value === "Color" ? "" : value;
        this.setState({ selectedColor: value });

        // Update existing vehicle object with selected color
        if (this.state.vehicle) {
            const updatedVehicle = { ...this.state.vehicle, color };
            this.setState({ vehicle: updatedVehicle });
            this.props.handleVIN(updatedVehicle);

            // If this is a previously saved vehicle (has Vehicle_ID), persist the color update
            if (updatedVehicle.id && color) {
                API.put(`/vehicles/${updatedVehicle.id}/color`, { color })
                    .catch(err => console.error('Error updating vehicle color:', err));
            }
        }
    };

    handleSelect = async (vehicleSelected) => {
        this.setState({ showList: false, isVinDecoded: false });

        // A previously-worked boat restores the MARINE cascade, not the car one.
        // Same contract as the automotive path: flip to the right panel, refill
        // every dropdown, re-emit the vehicle — but keyed on the boat reference
        // tables, and reusing the saved VIN rather than minting a new asset.
        if (isMarineVehicle(vehicleSelected)) {
            await this.selectSavedBoat(vehicleSelected);
            return;
        }

        const year = vehicleSelected.Year?.toString();
        if (!year) return;

        // Step 1: Set year and load makes
        this.setState({ selectedYear: year });
        await this.readMakes(year);

        // Step 2: Find matching make (case-insensitive) and load models
        let makeMatch = null;
        if (vehicleSelected.Make) {
            makeMatch = this.state.makes.find(make =>
                make.name.toLowerCase() === vehicleSelected.Make.toLowerCase()
            );
        }

        if (!makeMatch) {
            this.setState({ selectedMake: vehicleSelected.Make || "Make" });
            return;
        }

        this.setState({
            selectedMake: makeMatch.name,
            selectedMakeObj: makeMatch
        });
        await this.readModels([makeMatch]);

        // Step 3: Find matching model (case-insensitive) and load trims
        let modelMatch = null;
        if (vehicleSelected.Model) {
            modelMatch = this.state.models.find(model =>
                model.name.toLowerCase() === vehicleSelected.Model.toLowerCase()
            );
        }

        if (!modelMatch) {
            this.setState({ selectedModel: vehicleSelected.Model || "Model" });
            return;
        }

        this.setState({
            selectedModel: modelMatch.name,
            selectedModelObj: modelMatch,
            selectedSize: modelMatch.vehicleSize || vehicleSelected.size || ""
        });
        await this.readTrims([modelMatch]);

        // Step 4: Find matching trim (case-insensitive) and set body
        let trimMatch = null;
        if (vehicleSelected.Trim) {
            trimMatch = this.state.trims.find(trim =>
                trim.name.toLowerCase() === vehicleSelected.Trim.toLowerCase()
            );
        }

        // Validate body style against available options for this trim
        let bodyValue = vehicleSelected.body || "Style";
        if (trimMatch && bodyValue !== "Style") {
            const availableBodyStyles = [...new Set(
                this.state.trims
                    .filter(t => t.name === trimMatch.name)
                    .map(t => t.body_style)
            )];
            const bodyMatch = availableBodyStyles.find(b =>
                b.toLowerCase() === bodyValue.toLowerCase()
            );
            if (bodyMatch) bodyValue = bodyMatch;
        }

        const colorValue = vehicleSelected.color || vehicleSelected.Color || "";
        this.setState({
            selectedTrim: trimMatch ? trimMatch.name : (vehicleSelected.Trim || "Trim"),
            selectedTrimObj: trimMatch || null,
            selectedBody: bodyValue,
            selectedColor: colorValue || "Color"
        });

        // Step 5: Set VIN data
        const vin = vehicleSelected.VIN || vehicleSelected.vin || " ";
        this.setState({ data: { vin } });

        // Step 6: Build vehicle object using matched dropdown values
        var vehicle = {
            id: vehicleSelected.Vehicle_ID,
            year: year,
            make: makeMatch.name,
            model: modelMatch.name,
            trim: trimMatch ? trimMatch.name : vehicleSelected.Trim,
            id_car_trim: trimMatch?.id_car_trim || vehicleSelected.id_car_trim || null,
            body: bodyValue,
            color: colorValue,
            series: "",
            vin: vin,
            stock: vehicleSelected.stock,
            size: vehicleSelected.size || modelMatch.vehicleSize || "",
            body_style: trimMatch?.body_style || vehicleSelected.body_style || vehicleSelected.body || null,
            img_url: ""
        };

        this.setState({ vehicle });
        this.props.handleVIN(vehicle);
    }

// ---------------------------------------------------------------------------
// Marine (boat) entry — cascade is year -> make -> model, matching the
// automotive order. The model row carries the full dimensions and the vendor
// uuid, so /model/:uuid is never needed in the normal flow (deep-link/
// re-resolve only).
// ---------------------------------------------------------------------------

/**
 * Terminal rows are per MODEL-YEAR, not per generation. They carry `model_year`
 * and never `year_start`/`year_end`, so the old range read rendered every
 * option as "285 Conquest (undefined–)".
 *
 * The year is redundant here — the whole list is the year already picked — so
 * the suffix is the LOA when the reference row has one, and nothing when it
 * does not.
 *
 * Deliberately NOT flagged when absent. Reference coverage is partial, but
 * dimensions are only needed by the formulas that consume them: a MEASURED_AREA
 * service asks the estimator for a square footage and never reads LOA or beam.
 * Marking a model "dimensions not on file" would warn about a problem that only
 * sometimes exists and steer estimators away from boats they can quote fine.
 * The calculator prompts for whatever the chosen service's formula requires.
 *
 * MUST stay deterministic and unique per option: handleBoatModelChange matches
 * the selection BY THIS STRING. The server de-duplicates model names within a
 * make+year, so the name alone is already unique.
 */
boatModelLabel = (m) => {
    const loa = m.length_raw || (m.loa_ft ? `${m.loa_ft} ft` : null);
    return loa ? `${m.model_name} — ${loa}` : m.model_name;
};

/**
 * Restore a previously-worked boat picked from the customer-vehicle modal.
 *
 * Mirrors the automotive `handleSelect`: switch to the right panel, walk the
 * cascade matching each saved name, and re-emit the vehicle. Two differences,
 * both deliberate:
 *
 * 1. The SAVED VIN is reused, never re-minted. The VIN string is the
 *    request→asset join key, so minting here would fork the customer's boat
 *    into a second asset and orphan its history.
 * 2. Dimensions come from the reference tables, not from the modal row. That
 *    list is a thin projection and is not guaranteed to carry the marine
 *    fields; the cascade is authoritative and always has boat_class + uuid.
 */
selectSavedBoat = async (vehicleSelected) => {
    const savedVin = vehicleSelected.VIN || vehicleSelected.vin || null;
    const savedId = vehicleSelected.Vehicle_ID ?? vehicleSelected.id ?? "";
    const makeName = String(vehicleSelected.Make ?? "").trim();
    const modelName = String(vehicleSelected.Model ?? "").trim();
    const yearValue = String(vehicleSelected.Year ?? "").trim();

    this.setState({
        assetType: ASSET_TYPES.MARINE,
        marineError: "",
        boatVin: savedVin,
        boatVehicleId: savedId,
        data: { vin: savedVin || " " },
        // Clear the car cascade so a stale match cannot leak into the payload.
        selectedYear: "Year", selectedMake: "Make", selectedModel: "Model",
        selectedTrim: "Trim", selectedBody: "Style", selectedMakeObj: null,
        selectedModelObj: null, selectedTrimObj: null,
        selectedBoatYear: yearValue || "Year",
        boatMakes: [], selectedBoatMake: null, boatModels: [], selectedBoatModel: null,
        selectedBoatSize: "",
    });

    const notFound = (what) => this.setState({
        marineLoading: false,
        marineError: `This boat's ${what} is no longer in the reference data. Re-select it below.`,
    });

    if (!yearValue) return notFound('year');

    this.setState({ marineLoading: true });
    try {
        const boatMakes = await getBoatMakesForYear(yearValue);
        this.setState({ boatMakes });

        const makeRow = boatMakes.find(
            (m) => String(m.make_name || m.name || "").toLowerCase() === makeName.toLowerCase()
        );
        if (!makeRow) return notFound('make');

        const boatModels = await getBoatModels({
            makeId: makeRow.make_id ?? makeRow.id,
            make: makeRow.make_name || makeRow.name,
            year: yearValue,
        });
        this.setState({ selectedBoatMake: makeRow, boatModels });

        const modelRow = boatModels.find(
            (m) => String(m.model_name || "").toLowerCase() === modelName.toLowerCase()
        );
        if (!modelRow) return notFound('model');

        this.setState(
            {
                selectedBoatModel: modelRow,
                // The saved value wins; a boat saved without one takes the model's tier.
                selectedBoatSize:
                    boatSizeTier(vehicleSelected.Size ?? vehicleSelected.size) ||
                    boatSizeTier(modelRow.size_tier),
                marineLoading: false,
            },
            this.emitBoatVehicle
        );
    } catch (error) {
        this.setState({ marineLoading: false, marineError: error.message });
    }
};

handleAssetTypeChange = (assetType) => {
    if (assetType === this.state.assetType) return;
    // Reset BOTH cascades so a half-filled car selection can never leak into a
    // boat payload (or vice versa).
    this.setState({
        assetType,
        marineError: "",
        boatMakes: [], selectedBoatMake: null, boatModels: [], selectedBoatModel: null,
        selectedBoatYear: "Year", boatVin: null,
        boatVehicleId: "",
        selectedBoatSize: "",
        selectedYear: "Year", selectedMake: "Make", selectedModel: "Model",
        selectedTrim: "Trim", selectedBody: "Style", selectedMakeObj: null,
        selectedModelObj: null, selectedTrimObj: null, vehicle: "",
    });
    // Resetting the child's cascades above clears what's on screen, but the
    // parent already holds whatever vehicle this child last emitted via
    // handleVIN. Left alone, requestimateForm/walkInForm's completeness gate
    // keeps reading that stale vehicle and leaves Next enabled over now-empty
    // dropdowns — letting a boat payload submit against an asset type that was
    // just switched away from (or the reverse).
    if (this.props.handleVIN) {
        this.props.handleVIN("");
    }
};

handleBoatYearChange = async (event) => {
    const year = event.target.value;
    this.setState({
        selectedBoatYear: year,
        boatMakes: [], selectedBoatMake: null, boatModels: [], selectedBoatModel: null,
        marineError: "",
    });
    if (year === "Year") return;
    this.setState({ marineLoading: true });
    try {
        const boatMakes = await getBoatMakesForYear(year);
        // A year with no boats is a 200 with an empty array, never a 404. Left
        // unhandled that renders an empty Make picker with no explanation, which
        // reads as a broken page rather than a gap in the reference data.
        this.setState({
            boatMakes,
            marineLoading: false,
            marineError: boatMakes.length
                ? ""
                : `No boat makes on file for ${year}. Try another year.`,
        });
    } catch (error) {
        this.setState({ marineLoading: false, marineError: error.message });
    }
};

handleBoatMakeChange = async (event) => {
    const makeName = event.target.value;
    const selectedBoatMake = this.state.boatMakes.find(
        (m) => (m.make_name || m.name) === makeName
    ) || null;
    this.setState({
        selectedBoatMake, boatModels: [], selectedBoatModel: null, marineError: "",
    });
    if (!selectedBoatMake) return;
    this.setState({ marineLoading: true });
    try {
        const boatModels = await getBoatModels({
            makeId: selectedBoatMake.make_id ?? selectedBoatMake.id,
            make: selectedBoatMake.make_name || selectedBoatMake.name,
            year: this.state.selectedBoatYear,
        });
        this.setState({ boatModels, marineLoading: false });
    } catch (error) {
        this.setState({ marineLoading: false, marineError: error.message });
    }
};

/**
 * Picking a model COMPLETES the cascade — the model row already carries the
 * full dimensions and the vendor uuid, so there is no third network call.
 */
handleBoatModelChange = (event) => {
    const label = event.target.value;
    const selectedBoatModel = this.state.boatModels.find(
        (m) => this.boatModelLabel(m) === label
    ) || null;
    this.setState({
        selectedBoatModel,
        // The reference row settles the size — there is no estimator override.
        selectedBoatSize: boatSizeTier(selectedBoatModel?.size_tier),
        marineError: "",
    }, this.emitBoatVehicle);
};

/**
 * Build and emit the boat vehicle payload. Mirrors what handleBodyChange does
 * for cars: mint the placeholder VIN, shape the vehicle, hand it upward.
 *
 * boat_class is REQUIRED — vertical-surface and glass areas are derived from
 * per-class priors — so a row without one is surfaced rather than silently sent.
 */
emitBoatVehicle = () => {
    const row = this.state.selectedBoatModel;
    if (!row) return;
    // boat_class is NOT required to proceed. It gates the hull and glass PRIORS,
    // and `marine_class_priors` is empty — nothing consumes them today. An
    // estimator cannot supply a hull classification either, so refusing here
    // was a dead end with no action behind it. It rides through as null and the
    // calculator asks for whatever the chosen formula actually needs.

    const make = this.state.selectedBoatMake?.make_name || this.state.selectedBoatMake?.name || "";
    const model = row.model_name || "";
    // Mint ONCE and reuse across corrections. One estimate is one asset, so
    // re-picking a year, make or model must not change the boat's identity —
    // the VIN string is the request->asset join key.
    const boatVin = this.state.boatVin || mintVin(ASSET_TYPES.MARINE);

    // id_car_make / id_car_model / id_car_trim stay NULL for a boat.
    //
    // They FK into car_make / car_model / car_trim — real, populated tables — and
    // `vehicle` carries no foreign-key constraints, so MySQL accepts any integer
    // silently. Writing boat_make.id here does not mean "boat make 2"; it means
    // car_make #2 to every consumer that joins those columns (checkinModel,
    // modelModel, makesModel). Same shape as the dealer_id-in-a-User_id column
    // incident that misdirected invoice copies.
    //
    // The boat→reference link already exists and is stable across feed reloads:
    // `boat_model_uuid` + `boat_model_source`, the VENDOR identifiers, chosen
    // over auto-increment ids deliberately. Nothing is gained by duplicating a
    // churning id into the wrong table.
    const vehicle = {
        // Non-empty only when restoring a saved boat — links to the existing
        // vehicle row rather than forking the customer's asset in two.
        id: this.state.boatVehicleId || "",
        // The year comes from the dropdown selection. The row now also carries a
        // single `model_year` (reference rows are per model-year, not per
        // generation), but the dropdown is what the estimator chose and the two
        // agree by construction — the row was fetched FOR that year.
        year: this.state.selectedBoatYear,
        make,
        model,
        trim: "",
        id_car_make: null,
        id_car_model: null,
        id_car_trim: null,
        body: "",
        color: "",
        series: "",
        // Boats always mint — there is no real VIN to accept.
        vin: boatVin,
        stock: this.state.selectedStock,
        // From the model's reference `size_tier`. Blank (a model the feed has no
        // tier for) hides By-size services for this boat; Flat and Measured are
        // unaffected (spec D5/D7).
        size: this.state.selectedBoatSize || "",
        body_style: null,
        img_url: "",
        // 'boat', NOT 'marine' — vehicle.asset_type is ENUM('car','boat').
        // See VEHICLE_ASSET_TYPE in util/assetType.js for the collision.
        asset_type: VEHICLE_ASSET_TYPE.BOAT,
        // hull_height_in rides through from the reference row. It is null on a
        // boat nobody has quoted yet — no feed carries freeboard — and the area
        // calculator asks for it there. Once entered, the server learns it back
        // onto the reference row, so the next estimate for that model arrives
        // pre-filled. Asking again here would be a second prompt for the same
        // number, and the calculator is where it actually gets used.
        marine_attributes: buildMarineAttributes(row),
    };
    this.setState({ vehicle, boatVin });
    this.props.handleVIN(vehicle);
};

handleBodyChange = async (event) => {
    const { value } = event.target;
    this.setState({ selectedBody: value, isVinDecoded: false });
    
    const selectedTrimObj = this.state.selectedTrimObj || 
        this.state.trims.find(trim => trim.name === this.state.selectedTrim);
    
    var vehicle = {
        id: "",
        year: this.state.selectedYear,
        make: this.state.selectedMake,
        model: this.state.selectedModel,
        trim: this.state.selectedTrim,
        // Use your existing ID field logic
        id_car_make: this.state.selectedMakeObj?.id || this.state.selectedMakeObj?.id_car_make || null,
        id_car_model: this.state.selectedModelObj?.id || this.state.selectedModelObj?.id_car_model || null,
        id_car_trim: selectedTrimObj?.id_car_trim || selectedTrimObj?.id || null,
        body: value,
        color: "",
        series: "",
        vin: "",
        stock: this.state.selectedStock,
        size: selectedTrimObj ? selectedTrimObj.id_car_type : "",
        // The Style dropdown's options ARE body_style values (bodyList is built
        // from trim.body_style), so the selected `value` is a valid body style.
        // Fall back to it when the trim record itself has no body_style, so a
        // chosen Style always populates body_style — the field the catalog fetch
        // uses to filter by size. Never emit a null here off a real selection.
        body_style: selectedTrimObj?.body_style
            || (value && value !== "Style" && value !== "Body" ? value : null),
        img_url: ""
    }
    
    if (this.state.data.vin && this.state.data.vin.trim() !== "") {
        // Trim any leading/trailing spaces before saving to database
        vehicle.vin = this.state.data.vin.trim()
    } else {
        // Was moment("YYYYMMDDHHMMSS") — moment's MM is MONTH, so the stamp
        // repeated the month and carried no minutes, colliding within an hour.
        vehicle.vin = mintVin(
          vehicle.make === "Other car" ? ASSET_TYPES.OTHER : ASSET_TYPES.AUTO
        );
    }

    // Check for duplicates before proceeding
    const duplicates = await this.checkForDuplicateVehicle(vehicle);
    
    if (duplicates && duplicates.length > 0) {
        // Build detailed alert message
        let alertMessage = `We found ${duplicates.length} existing `;
        
        // Determine if it's estimates or jobs based on status
        const hasEstimates = duplicates.some(dup => dup.current_state === 'new');
        const hasJobs = duplicates.some(dup => dup.current_state !== 'new');
        
        if (hasEstimates && hasJobs) {
            alertMessage += 'estimate(s) and job(s)';
        } else if (hasEstimates) {
            alertMessage += 'estimate(s)';
        } else {
            alertMessage += 'job(s)';
        }
        
        alertMessage += ` for this ${vehicle.year} ${vehicle.make} ${vehicle.model}:\n\n`;
        
        // Add details for each duplicate
        duplicates.forEach((dup, index) => {
            const statusType = dup.current_state === 'new' ? 'Estimate' : 'Job';
            const date = new Date(dup.available_date).toLocaleDateString();
            alertMessage += `${index + 1}. ${statusType} created for ${date}\n`;
        });
        
        alertMessage += '\nDo you want to continue creating a new request?';
        
        const proceed = window.confirm(alertMessage);
        
        if (!proceed) {
            return; // Stop the process
        }
    }

    console.log("VehicleSelectForm - Final vehicle object with IDs:", vehicle);
    this.setState({ vehicle });
    this.props.handleVIN(vehicle);
}

    render() {

            // Don't show generated VINs (NV* / OT* / BT*) to the user
        const displayVin = isPlaceholderVin(this.state.data.vin) ? '' : this.state.data.vin;

        var makeList = this.state.makes.map(a => a.name);
        makeList.unshift("Make");
        var modelList = this.state.models.map(a => a.name);
        modelList.unshift("Model");
        
        // Get unique trim names
        var trimList = [...new Set(this.state.trims.map((item) => item.name))];
        trimList.unshift("Trim");
        
        // Filter body styles based on selected trim if available
        let bodyList;
        if (this.state.selectedTrim !== "Trim") {
            const selectedTrimTrims = this.state.trims.filter(trim => trim.name === this.state.selectedTrim);
            bodyList = [...new Set(selectedTrimTrims.map(item => item.body_style))];
        } else {
            bodyList = [...new Set(this.state.trims.map(item => item.body_style))];
        }
        bodyList.unshift("Style");

        const { loading } = this.state;
        // Every marine branch in this render is guarded on isAuto. With marine
        // disabled the toggle never renders and assetType stays 'auto', so the
        // automotive path is byte-identical to before this feature existed.
        const isAuto = this.state.assetType !== ASSET_TYPES.MARINE;

        if ((this.props.currentStep === 2 && this.props.userType === "installer") || 
            (this.props.currentStep === 2 && this.props.userType === "dealer")) {
            return (
                <React.Fragment>
                    <form className="form-container p-5">
                        {/* Asset-type toggle. Rendered ONLY for marine-capable
                            locations; a car-only shop never sees it and
                            assetType stays 'auto'. */}
                        {/* No toggle when the wizard is typed: a marine dealership
                            raises boat work only, so there is nothing to choose. */}
                        {!this.props.forcedAssetType && isMarineEnabled() && (
                            <div className="form-row justify-content-md-center mb-3">
                                <div className="btn-group" role="group" aria-label="Asset type">
                                    <button
                                        type="button"
                                        className={`btn ${isAuto ? 'jv-btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => this.handleAssetTypeChange(ASSET_TYPES.AUTO)}
                                    >
                                        Road
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${!isAuto ? 'jv-btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => this.handleAssetTypeChange(ASSET_TYPES.MARINE)}
                                    >
                                        Water
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Above the fork on purpose: a dealer reconciles a BOAT
                            against their inventory the same way they do a car, and
                            requestimateForm submits stock_number for both. */}
                        {this.props.userType === "dealer" && (
                            <div className="form-row justify-content-md-center">
                                <div className="form-group col-md-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="stockNumber"
                                        placeholder="Stock # (Optional)"
                                        value={this.state.stockNumber || ""}
                                        onChange={this.handleStockChange}
                                        maxLength={50}
                                    />
                                </div>
                            </div>
                        )}

                        {!isAuto ? (
                          <>
                            {this.state.marineError && (
                                <div className="form-row justify-content-md-center">
                                    <div className="col-md-8">
                                        <div className="alert alert-danger">{this.state.marineError}</div>
                                    </div>
                                </div>
                            )}
                            <div className="form-row justify-content-md-center">
                                <div className="form-group col-md-2">
                                    <Select
                                        name="boatYear"
                                        label=" "
                                        options={this.state.years}
                                        value={this.state.selectedBoatYear}
                                        onChange={this.handleBoatYearChange}
                                        hideEmpty
                                    />
                                </div>
                                <div className="form-group col-md-3">
                                    <Select
                                        name="boatMakes"
                                        label=" "
                                        options={["Make", ...this.state.boatMakes.map((m) => m.make_name || m.name)]}
                                        value={this.state.selectedBoatMake?.make_name || this.state.selectedBoatMake?.name || "Make"}
                                        onChange={this.handleBoatMakeChange}
                                        hideEmpty
                                    />
                                </div>
                                <div className="form-group col-md-4">
                                    <Select
                                        name="boatModels"
                                        label=" "
                                        options={["Model", ...this.state.boatModels.map((m) => this.boatModelLabel(m))]}
                                        value={this.state.selectedBoatModel ? this.boatModelLabel(this.state.selectedBoatModel) : "Model"}
                                        onChange={this.handleBoatModelChange}
                                        hideEmpty
                                    />
                                </div>
                                {/* Boat size is NOT asked for — the reference row
                                    carries `size_tier`, so the model pick already
                                    settles it. See handleBoatModelChange. */}
                                {/* HIN is deliberately NOT collected here — it is
                                    captured at check-in, where the boat is
                                    physically present. See MARINE_ASSETS_SPEC §3. */}
                            </div>
                            {!this.state.selectedBoatModel && (
                                <div className="form-row justify-content-md-center">
                                    <h6 className="text-center text-muted">Select Year, Make and Model to continue</h6>
                                </div>
                            )}
                            {this.state.marineLoading && (
                                <div className="form-row justify-content-md-center">
                                    <i className="fa-sharp fa-regular fa-refresh fa-spin" />
                                </div>
                            )}
                          </>
                        ) : (
                          <>
                        <div className="form-row justify-content-md-center">
                            <h6 className="text-center">Enter a VIN to populate fields or choose manually below</h6>
                        </div>
                        <div className="form-row justify-content-md-center">
                            <div className="form-group col-md-3">
                                <input
                                    type='text'
                                    className="form-control"
                                    name='vin'
                                    value={displayVin}
                                    onChange={this.handleChange} 
                                    error={this.state.errors['vin']} 
                                />
                                {this.state.errors['vin'] && 
                                    <div className="alert alert-danger">{this.state.errors['vin']}</div>
                                }
                            </div>
                            <div className="form-group col-md-2">
                                <button 
                                    type="button" 
                                    className="jv-btn jv-btn-primary btn-block"
                                    onClick={this.doSubmit}
                                    disabled={this.validate()}
                                >
                                    {loading && <span><i className="fa-sharp fa-regular fa-refresh fa-spin" /></span>}
                                    {!loading && <span>Decode VIN</span>}
                                </button>
                            </div>
                        </div>
                        
                        <div className="form-row justify-content-md-center">
                            <div className="form-group col-md-2">
                                <Select
                                    name="years"
                                    label=" "
                                    options={this.state.years}
                                    value={this.state.selectedYear}
                                    onChange={this.handleYearChange}
                                    hideEmpty
                                />
                            </div>
                            <div className="form-group col-md-2">
                                <Select
                                    name="makes"
                                    label=" "
                                    options={makeList}
                                    value={this.state.selectedMake}
                                    onChange={this.handleMakeChange}
                                    hideEmpty
                                />
                            </div>
                            <div className="form-group col-md-2">
                                <Select
                                    name="models"
                                    label=" "
                                    options={modelList}
                                    value={this.state.selectedModel}
                                    onChange={this.handleModelChange}
                                    hideEmpty
                                />
                            </div>
                            <div className="form-group col-md-2">
                                <Select
                                    name="trims"
                                    label=" "
                                    options={trimList}
                                    value={this.state.selectedTrim}
                                    onChange={this.handleTrimChange}
                                    hideEmpty
                                />
                            </div>
                            <div className="form-group col-md-2">
                                <Select
                                    name="body"
                                    label=" "
                                    options={bodyList}
                                    value={this.state.selectedBody}
                                    onChange={this.handleBodyChange}
                                    hideEmpty
                                />
                            </div>
                            <div className="form-group col-md-2">
                                <Select
                                    name="color"
                                    label=" "
                                    options={["Color", "Black", "Blue", "Brown", "Gold", "Gray", "Green", "Orange", "Purple", "Red", "Silver", "White", "Yellow"]}
                                    value={this.state.selectedColor}
                                    onChange={this.handleColorChange}
                                    hideEmpty
                                />
                                <small className="form-text text-muted text-center">Optional</small>
                            </div>
                        </div>
                        <div className="form-row justify-content-md-center">
                            <h6 className="text-center text-muted">Select Year, Make, Model, Trim, and Style to continue</h6>
                        </div>
                          </>
                        )}
                    </form>

                    {/* A saved/legacy vehicle can arrive with no body_style, which the
                        catalog needs to filter by size. Prompt the user to pick a Style
                        (Next stays disabled until they do — walkInForm.checkVehicleComplete). */}
                    {isAuto && this.state.vehicle && !this.state.vehicle.body_style &&
                        <div className="form-row justify-content-md-center">
                            <div className="col-md-8">
                                <div className="alert alert-warning text-center">
                                    This vehicle is missing a body style. Please select a <strong>Style</strong> above to continue.
                                </div>
                            </div>
                        </div>
                    }

                    {isAuto && this.state.selectedBody !== "Style" &&
                        <div>
                            <h5 className="text-center bold p-5">Vehicle Selected</h5>
                            <div className="col-lg-12 bold text-center">
                                <p>
                                    {this.state.selectedColor !== "Color" && `${this.state.selectedColor} `}{this.state.selectedYear} {this.state.selectedMake} {this.state.selectedModel} {this.state.selectedTrim} {this.state.selectedBody}
                                    {/* Only show VIN if it's a real VIN (not generated) */}
                                    {!isPlaceholderVin(this.state.data.vin) && (
                                        <>
                                            <br />
                                            <small>VIN: {this.state.data.vin}</small>
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    }

                    {!isAuto && this.state.selectedBoatModel && !this.state.marineError &&
                        <div>
                            <h5 className="text-center bold p-5">Craft Selected</h5>
                            <div className="col-lg-12 bold text-center">
                                <p>
                                    {this.state.selectedBoatYear}{' '}
                                    {this.state.selectedBoatMake?.make_name || this.state.selectedBoatMake?.name}{' '}
                                    {this.state.selectedBoatModel.model_name}
                                </p>
                            </div>
                        </div>
                    }
                    <SelectCustomerVehicle
                        showList={this.state.showList}
                        onHide={this.handleClose}
                        vehicles={this.state.custVehicles}
                        onSelect={this.handleSelect}
                    />
                </React.Fragment>
            )
        } else {
            return null
        }
    }
}